import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { CurrentUserService } from './current-user.service';

export enum UserContextEnum {
  Coach = 'COACH',
  Coachee = 'COACHEE',
  Unset = 'UNSET',
}

@Injectable({
  providedIn: 'root',
})
export class UserContextService implements OnDestroy {
  private readonly CONTEXT_STORAGE_KEY = 'user_context';

  private userContextSubject = new BehaviorSubject<UserContextEnum>(
    this.getStoredContext()
  );

  public userContext$ = this.userContextSubject.asObservable();

  private currentUserSubscription: Subscription;
  private storageHandler = (event: StorageEvent) => {
    if (event.key === this.CONTEXT_STORAGE_KEY) {
      this.userContextSubject.next(
        (event.newValue as UserContextEnum) || UserContextEnum.Unset
      );
    }
  };

  constructor(private currentUserService: CurrentUserService) {
    this.currentUserSubscription =
      this.currentUserService.currentUser$.subscribe(user => {
        if (user) {
          this.setUserContext(
            user.isCoach ? UserContextEnum.Coach : UserContextEnum.Coachee
          );
        } else {
          this.resetUserContext();
        }
      });

    // Initialize synchronization between tabs
    window.addEventListener('storage', this.storageHandler);
  }

  ngOnDestroy(): void {
    this.currentUserSubscription.unsubscribe();
    window.removeEventListener('storage', this.storageHandler);
  }

  private getStoredContext(): UserContextEnum {
    const storedContext = sessionStorage.getItem(this.CONTEXT_STORAGE_KEY);
    return (storedContext as UserContextEnum) || UserContextEnum.Unset;
  }

  setUserContext(context: UserContextEnum): void {
    sessionStorage.setItem(this.CONTEXT_STORAGE_KEY, context);
    this.userContextSubject.next(context);
  }

  getCurrentUserContext(): UserContextEnum {
    return this.userContextSubject.value;
  }

  isContextSet(): boolean {
    return this.getCurrentUserContext() !== UserContextEnum.Unset;
  }

  resetUserContext(): void {
    sessionStorage.removeItem(this.CONTEXT_STORAGE_KEY);
    this.userContextSubject.next(UserContextEnum.Coachee);
  }
}
